import { createStore } from "vuex";
import api from "@/utils/api";
import { initWebSocket } from "@/utils/wsHelper";
import { saveData, loadData, removeData } from "@/utils/storageHelper";
import { localeToLanguage } from "@/utils/localeMapper";
import onboarding from "./modules/onboarding";
import errorDialog from "./modules/error";
import terminal from "./modules/terminal";
import menu from "./modules/menu";
import order from "./modules/order";
import customer from "./modules/customer";
import location from "./modules/location";
import dialog from "./modules/dialog";
import orderItem from "./modules/orderItem";
import tip from "./modules/tip";
import receipt from "./modules/receipt";
import admin from "./modules/admin";
import coupon from "./modules/coupon";
import logger from "@/utils/logger";

const _fetch = async (what, authToken) => {
  try {
    const pairingMethod = store.state.terminal.pairingMethod;
    const merchantId = store.state.terminal.previewMid;
    const isPreview = pairingMethod === 'mid';
    const endpoint = isPreview ? `v2/preview/${merchantId}/${what}` : `v2/${what}`;
    const response = await api.callAPI("GET", endpoint, null, isPreview ? null : authToken);
    if (!response.ok && response.status === 401) {
      store.dispatch('handleAuthError');
    }
    return response;
  } catch (error) {
    store.dispatch('handleAuthError');
    throw error;
  }
};

const store = createStore({
  modules: {
    onboarding,
    errorDialog,
    terminal,
    location,
    menu,
    order,
    customer,
    dialog,
    orderItem,
    tip,
    receipt,
    admin,
    coupon
  },
  state() {
    return {
      terminalPaired: false,
      terminalAuthenticated: false,
      currentLanguage: 'en',
      feVersion: '2.2.31',
      currentVersion: '1.0.0',
      startOver: false,
      initialized: false,
      isWebSocketConnected: false,
      errorDialogState: null,
      isLoading: false,
      loadingMessage: null,
      cachedMenuData: null,
      cachedMenuHash: null,
      brandColor: "#295aea",
      screenOrientation: "horizontal",
      idleTimeoutSeconds: 60,
      translator: null,
      isKiosk: window.location.pathname.includes('kiosk/'),
      setupMode: {
        active: false,
        screen: null,
        data: null
      },
      currentTime: new Date(), // Reactive current time for schedule evaluation
      timeUpdateInterval: null, // Store the interval ID for cleanup
    };
  },
  getters: {
    context: () => {
      const context = window.location.pathname;
      // to string kiosk, admin, public
      if (context.includes('/kiosk')) {
        return 'kiosk';
      } else if (context.includes('/admin')) {
        return 'admin';
      } else {
        return 'public';
      }
    },
    initialized: (state) => state.initialized,
    feVersion: (state) => state.feVersion,
    errorDialogState: (state) => state.errorDialogState,
    loadingMessage: (state) => state.loadingMessage,
    isLoading: (state) => state.isLoading,
    isWebSocketConnected: (state) => state.isWebSocketConnected,
    screenOrientation: (state) => state.screenOrientation,
    isMobile() {
      const userAgent = navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod|android|blackberry|mini|windows\sce|palm/i.test(userAgent);
    },
    hasSDK: () => {
      // eslint-disable-next-line no-undef
      return typeof Shift4Kiosk !== "undefined";
    },
    translate: (state) => (key) => state.translator ? state.translator.translate(key) : key,
    currentLanguage: (state) => state.currentLanguage,
    isUsLocale: (state) => state.currentLanguage === 'en',
    currentTime: (state) => state.currentTime,
    isPreview: (state) => state.preview,
  },
  mutations: {
    SET_LANGUAGE(state, language) {
      state.currentLanguage = language;
      if (state.translator) {
        state.translator.setLanguage(language);
      }
    },
    SET_VERSION(state, version) {
      state.currentVersion = version;
    },
    SET_INITIALIZED(state, initialized) {
      state.initialized = initialized;
    },
    SET_LOCATION_ID(state, id) {
      state.locationId = id;
    },
    SET_CACHED_MENU_DATA(state, menuData) {
      state.cachedMenuData = menuData;
    },
    SET_CACHED_MENU_HASH(state, hash) {
      state.cachedMenuHash = hash;
    },
    SET_LOADING(state, { visible, message = null }) {
      state.isLoading = visible;
      state.loadingMessage = message || '';
    },
    SET_WEBSOCKET_CONNECTION(state, isConnected) {
      state.isWebSocketConnected = isConnected;
    },
    SET_ERROR(state, errorDialog) {
      console.log('SET_ERROR', errorDialog)
      state.errorDialogState = errorDialog;
    },
    SET_SCREEN_ORIENTATION(state, orientation) {
      state.screenOrientation = orientation;
    },
    SET_IDLE_TIMEOUT_SECONDS(state, timeout) {
      state.idleTimeoutSeconds = timeout;
    },
    SET_TRANSLATOR(state, translator) {
      state.translator = translator;
    },
    SET_SETUP_MODE(state, { active, screen, data }) {
      state.setupMode = { active, screen, data };
    },
    SET_CURRENT_TIME(state, time) {
      state.currentTime = time;
    },
    SET_TIME_UPDATE_INTERVAL(state, intervalId) {
      state.timeUpdateInterval = intervalId;
    },
  },
  actions: {
    async fetchCanStartOrder({ state }, orderTypeId = null) {
      let endpoint = 'order/can-start';
      if (orderTypeId) {
        endpoint += `?orderTypeId=${orderTypeId}`;
      }
      const res = await _fetch(endpoint, state.terminal.apiToken);
      return res;
    },
    setLanguage({ commit }, language) {
      commit('SET_LANGUAGE', language);
    },
    handleAuthError({ commit }) {
      commit('terminal/SET_AUTHENTICATED', false);
    },
    async checkVersion({ commit, state, getters }, { version }) {
      if (state.currentVersion !== version) {
        const isOnline = navigator.onLine;
        const isOrderInProgress = getters['order/orderType'];

        //const pageExists = await fetch(window.location.href).then(response => response.ok).catch(() => false);
        const pageExists = true;

        const shouldRefresh = isOnline && !isOrderInProgress && pageExists;

        if (shouldRefresh) {
          logger.info("New version detected. Reloading page...");
          //window.location.reload(true);
        } else {

          commit('SET_VERSION', version);
        }
      }
    },
    loading({ commit, state }, key) {
      let message = key;

      try {
        message = state.translator ? state.translator.translate(key) : key
      } catch (error) {
        console.error('Error translating loading message', error);
      }

      commit("SET_LOADING", {
        visible: true,
        message,
      });
    },
    doneLoading({ commit }) {
      commit("SET_LOADING", {
        message: null,
        visible: false,
      });
    },
    async loadStockData({ commit, state }) {
      const stockData = await _fetch("menu/stock", state.terminal.apiToken);
      commit("menu/SET_STOCK", stockData?.items || []);
    },
    async loadMenuData({ commit, state, dispatch }) {
      await dispatch('checkAndUpdateMenuHash');

      if (state.cachedMenuData) {
        commit("menu/SET_MENU", state.cachedMenuData);
      } else {
        const menuData = await _fetch("menu", state.terminal.apiToken);
        commit("menu/SET_MENU", menuData);
        state.cachedMenuData = menuData;
        saveData('cachedMenuData', menuData);
      }
    },
    async checkAndUpdateMenuHash({ state }) {
      const result = await _fetch("menu/hash", state.terminal.apiToken);

      const hash = result;

      if (hash !== state.cachedMenuHash) {
        state.cachedMenuData = null;
        removeData('cachedMenuData');
        state.cachedMenuHash = hash;
        saveData('cachedMenuHash', hash);
      }
    },
    setTranslator({ commit }, { translator }) {
      try {
        if (translator) {
          commit('SET_TRANSLATOR', translator);
        } else {
          console.error('Translator instance not found');
        }
      } catch (error) {
        console.error('Error setting config texts', error);
      }
    },
    async loadLocationData({ state, commit }) {
      try {
        this.dispatch("loading", 'loadingLocationInformation');
        let hash = null;
        if (state.cachedMenuData) {
          hash = state.cachedMenuHash;
        }
        const all = await _fetch(`?hash=${hash}&version=${state.feVersion}`, state.terminal.apiToken);

        if (all.terminal) {
          commit('terminal/SET_TERMINAL_DETAILS', all.terminal, { root: true });
        }

        commit("location/SET_CONFIG", all.config);

        // Reschedule the kiosk daily restart when config arrives (it includes the update window hour).
        if (state.isKiosk) {
          import("@/utils/scheduleDailyRestart").then(({ scheduleDailyRestart }) => scheduleDailyRestart());
        }
        commit("location/SET_HAS_COUPONS", all.hasCoupons);
        commit("location/SET_DETAILS", all.location);
        commit("SET_IDLE_TIMEOUT_SECONDS", all.config.idleTimeoutSeconds || 60);

        if (all.config?.text && state.translator) {
          state.translator.setConfigTexts(all.config.text);
        }

        // Language selection priority:
        // 1. User-selected language (if changed from initial 'en')
        // 2. Language support config (if enabled)
        // 3. Locale-based language derivation (fallback)
        const languageSupport = all.config?.languageSupport || {};
        const configuredDefaultLanguage = languageSupport.defaultLanguage || 'en';
        const localeBasedLanguage = localeToLanguage(all.config?.locale);

        // Only auto-set language if user hasn't manually changed it (still on 'en')
        if (state.currentLanguage === 'en') {
          let newLanguage = 'en';

          if (languageSupport.enabled) {
            // Use configured default language when language support is enabled
            newLanguage = configuredDefaultLanguage;
          } else {
            // Fall back to locale-based language detection
            newLanguage = localeBasedLanguage;
          }

          if (newLanguage !== state.currentLanguage) {
            commit('SET_LANGUAGE', newLanguage);
          }
        } else if (!languageSupport.enabled && state.currentLanguage !== localeBasedLanguage) {
          // If language support is disabled and current language doesn't match locale, reset to locale-based
          commit('SET_LANGUAGE', localeBasedLanguage);
        }

        if (all.location.payment_token) {
          commit("SET_LOCATION_ID", all.location.id);
        }

        if (all.menu) {
          commit("menu/SET_MENU", all.menu);
          state.cachedMenuData = all.menu;
          saveData('cachedMenuData', all.menu);
          saveData('cachedMenuHash', all.hash);
          state.cachedMenuHash = all.hash;
        } else {
          if (state.cachedMenuData) {
            commit("menu/SET_MENU", state.cachedMenuData);
          }
        }

        if (all.stock?.items) {
          commit("menu/SET_STOCK", all.stock.items);
        }
      } finally {
        this.dispatch("doneLoading");
      }
    },
    async initKiosk({ dispatch, rootState }) {

      // first is it paired?
      this.terminalPaired = rootState.terminal.terminalDetails.locationId ? true : false;

      // then is it authenticated?
      this.terminalAuthenticated = await dispatch('terminal/checkAuthenticated');



    },
    async initializeLocation({ commit, dispatch, rootState }) {
      const cachedMenuData = loadData('cachedMenuData');
      const cachedMenuHash = loadData('cachedMenuHash');
      commit('SET_CACHED_MENU_DATA', cachedMenuData);
      commit('SET_CACHED_MENU_HASH', cachedMenuHash);
      await dispatch("loadLocationData");
      dispatch("initWebSocketNow", { locationId: rootState.terminal.locationId, terminalId: rootState?.terminal?.terminalDetails?.id });
      
      // Start reactive time updates for schedule evaluation
      dispatch("startTimeUpdates");
      
      commit("SET_INITIALIZED", true);
      commit("SET_LOCATION_ID", rootState.terminal.locationId);

    },
    async initializeLocationOld({ commit, dispatch, rootState }) {
      if (!rootState.terminal.apiToken) return;
      if (rootState.terminal.apiToken) {
        commit('terminal/SET_API_TOKEN', rootState.terminal.apiToken);
      }
      commit("SET_INITIALIZED", false);
      if (!rootState.terminal.locationId) {
        commit("terminal/SET_AUTHENTICATED", false);
      }
      commit("SET_LOCATION_ID", rootState.terminal.locationId);

      const cachedMenuData = loadData('cachedMenuData');
      const cachedMenuHash = loadData('cachedMenuHash');

      commit('SET_CACHED_MENU_DATA', cachedMenuData);
      commit('SET_CACHED_MENU_HASH', cachedMenuHash);

      try {
        await dispatch("loadLocationData");
        dispatch("initWebSocketNow", { locationId: rootState.locationId, terminalId: rootState?.terminal?.terminalDetails?.id });
        commit("SET_INITIALIZED", true);
        return true;
      } catch (error) {
        // if any 4XX error, try to register the terminal
        if (error.message.includes("401") || error.message.includes("403")) {
          commit("terminal/SET_AUTHENTICATED", false);
          commit("SET_LOCATION_ID", null);
          // eslint-disable-next-line no-undef
          if (typeof Shift4Kiosk !== 'undefined' && typeof Shift4Kiosk.deviceConfig === 'function') {
            // eslint-disable-next-line no-undef
            const config = JSON.parse(Shift4Kiosk.deviceConfig());
            await dispatch("terminal/registerTerminalSerial", config.serialNumber);
            if (await dispatch("isTerminalAuthenticated")) {
              await dispatch("initializeLocation");
              commit("SET_INITIALIZED", true);
              return true;
            }
          }
        }
        logger.error("Error loading initial data:", error);
        return false;
      }
    },
    async isTerminalAuthenticated({ state }) {
      try {
        let hash = null;
        if (state.cachedMenuData) {
          hash = state.cachedMenuHash;
        }
        await _fetch(`?hash=${hash}&version=${state.feVersion}`, state.terminal.apiToken);
        return true;
      } catch (error) {
        return false;
      }
    },
    async initWebSocketNow({ commit, state }, { locationId, terminalId }) {
      if (this.websocket && this.websocket.close) {
        this.websocket?.close();
      }

      const wsCallbacks = {
        onOpen: () => commit("SET_WEBSOCKET_CONNECTION", true),
        onMessage: (event) => this.dispatch("handleWebhookEvent", event),
        onError: (error) => logger.error("WebSocket error:", error),
        onClose: () => commit("SET_WEBSOCKET_CONNECTION", false),
      };

      const location = locationId || state.locationId;

      this.websocket = initWebSocket(location, terminalId, wsCallbacks);  // Pass terminalId to the WebSocket helper
    },
    async handleWebhookEvent({ commit, dispatch, state }, event) {
      logger.debug("Webhook event received:", event);
      const data = JSON.parse(event.data);
      const payload = data?.payload || {};
      const status = payload?.status || "UNKNOWN";
      switch (data.type) {
        case 'activeOrderUpdate':
          dispatch("admin/activeCount", data.count);
          break;
        case "REFRESH_DASHBOARD":
          dispatch("admin/fetchSummary", { period: "today" });
          break;
        case "ORDER_STATUS_CHANGED":
          dispatch("order/updateOrderStatus", {
            status,
            reasons: payload?.reasons || [],
            callNumber: payload?.callNumber || null,
            receiptData: payload?.receiptData || null,
          });
          this.dispatch("doneLoading");
          break;
        case "REFRESH_MENU":
          await dispatch("loadMenuData", state.locationId);
          break;
        case "HAS_COUPONS":
          console.log('HAS_COUPONS', state.locationId, JSON.parse(data.payload));
          await commit("location/SET_HAS_COUPONS", JSON.parse(data.payload));
          break;
        case "REFRESH_STOCK":
          await dispatch("loadStockData");
          break;
        case "UPDATE_CONFIG": {
          dispatch("loading");
          // eslint-disable-next-line no-case-declarations
          const config = JSON.parse(data.payload);
          
          // Update kiosk config if in kiosk context
          if (state.isKiosk) {
            await commit("location/SET_CONFIG", config);
            if (config.text && state.translator) {
              state.translator.setConfigTexts(config.text);
            }
            // Reschedule the kiosk daily restart if the update window hour changed.
            import("@/utils/scheduleDailyRestart").then(({ scheduleDailyRestart }) => scheduleDailyRestart());
          }
          
          // Update admin config if in admin context
          const isAdminContext = window.location.pathname.includes('/admin');
          if (isAdminContext && store.getters['admin/isLoggedIn']) {
            await commit("admin/SET_CONFIG", config);
            await commit("admin/SET_CONFIG_VERSION", config.version);
            // Trigger language update if locale changed
            dispatch("admin/updateLanguageFromConfig", config);
          }
          
          dispatch("doneLoading", false);
          break;
        }
        case "RESTART_TERMINALS":
          window.location.reload();
          break;
        case "SETUP_MODE":
          if (state.isKiosk) {
            commit('SET_SETUP_MODE', { active: true, screen: data.screen, data: data.payload });
          }
          break;
        case "RESET_SETUP_MODE":
          commit('SET_SETUP_MODE', { active: false, screen: null, data: null });
          break;
      }
    },
    retryConnectWebsocket() {
      if (this.websocket && this.websocket.manualReconnect) {
        this.websocket.manualReconnect();
      } else {
        const locationId = this.state.locationId || this.state.terminal.locationId;
        const terminalId = this.state.terminal.terminalId;
        this.dispatch('initWebSocketNow', { locationId, terminalId });
      }
    },
    async loadPublicLocationData({ commit, dispatch }) {

      try {
        await dispatch("loadLocationData");
        //dispatch("initWebSocketNow", { locationId: locationId, terminalId: rootState?.terminal?.terminalDetails?.id });
        
        // Start reactive time updates for schedule evaluation
        dispatch("startTimeUpdates");
        
        commit("SET_INITIALIZED", true);
        return true;
      } catch (error) {
        console.error("Error loading public location data:", error);
      }
    },
    startTimeUpdates({ commit, state }) {
      // Clear any existing interval
      if (state.timeUpdateInterval) {
        clearInterval(state.timeUpdateInterval);
      }
      
      // Update time immediately when starting updates
      commit('SET_CURRENT_TIME', new Date());
      
      // Update time every 15 seconds (15000ms) to refresh schedule evaluations more frequently
      const intervalId = setInterval(() => {
        commit('SET_CURRENT_TIME', new Date());
      }, 15000);
      
      commit('SET_TIME_UPDATE_INTERVAL', intervalId);
    },
    stopTimeUpdates({ commit, state }) {
      if (state.timeUpdateInterval) {
        clearInterval(state.timeUpdateInterval);
        commit('SET_TIME_UPDATE_INTERVAL', null);
      }
    },
    updateTimeNow({ commit }) {
      // Manual time update (useful for testing schedule changes)
      commit('SET_CURRENT_TIME', new Date());
    },
  },
});

export default store;
